import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { formatters } from 'folio-common-utils';
import { colors } from 'folio-design-tokens';
import * as React from 'react';
import { CheckmarkRoundIcon, InfoIcon } from '../../icons';
import { type RootState, useSelector } from '../../state';

const Wrapper = styled.p`
  margin: 20px 0;
`;

export const OwnershipSummaryIcon = styled.span`
  display: inline-block;
  vertical-align: middle;
  margin-right: 0.6em;
`;

const AlignMiddleSpan = styled.span`
  vertical-align: middle;
`;

const middleIcon = css`
  display: block;
`;

const StyledInfoIcon = styled(InfoIcon)`
  ${middleIcon};
`;

const StyledCheckedmarkIcon = styled(CheckmarkRoundIcon)`
  ${middleIcon};
`;

function selector(state: RootState) {
  return {
    capital: Number(state.editor.coreInfo.capital),
    equity: Object.values(state.editor.equity).reduce(
      (prev: number, curr) => (curr !== '' ? prev + Number(curr) : prev),
      0,
    ),
    nullOwner: Object.values(state.editor.equity).some(
      equity => Number(equity) === 0,
    ),
  };
}

export const OwnershipSummary: React.FC = () => {
  const { capital, equity, nullOwner } = useSelector(selector);

  if (capital < 30000) {
    return (
      <Wrapper data-testid="OwnershipSummary">
        <OwnershipSummaryIcon>
          <StyledInfoIcon color={colors.blue} />
        </OwnershipSummaryIcon>
        <AlignMiddleSpan>
          Husk at aksjekapital må være minst{' '}
          {formatters.formatAmount(30000, { currency: 'kroner' })}{' '}
        </AlignMiddleSpan>
      </Wrapper>
    );
  }

  if (nullOwner) {
    return (
      <Wrapper data-testid="OwnershipSummary">
        <OwnershipSummaryIcon>
          <StyledInfoIcon color={colors.blue} />
        </OwnershipSummaryIcon>
        <AlignMiddleSpan>Eierandel kan ikke være 0&nbsp;kr</AlignMiddleSpan>
      </Wrapper>
    );
  }

  const diff = equity - capital;

  const text =
    diff === 0
      ? 'Alt er nøyaktig fordelt!'
      : diff > 0
      ? `${formatters.formatNumber(diff)} kroner må fjernes fra fordelingen.`
      : `${formatters.formatNumber(Math.abs(diff))} kroner igjen å fordele.`;

  return (
    <Wrapper data-testid="OwnershipSummary">
      <OwnershipSummaryIcon>
        {diff === 0 ? (
          <StyledCheckedmarkIcon color={colors.green} />
        ) : (
          <StyledInfoIcon color={colors.blue} />
        )}
      </OwnershipSummaryIcon>
      <AlignMiddleSpan>{text}</AlignMiddleSpan>
    </Wrapper>
  );
};
